import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { usePermission } from '@/Hooks/usePermission';
import { useAppDispatch } from '@/Redux/store';
import { useConfirm } from '@/components/ui/confirm';
import { useBranches } from '@/Pages/Operations/shared';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import PanelResult from '@/Pages/Integrations/components/PanelResult';
import SecretRevealDialog from '@/Pages/Integrations/components/SecretRevealDialog';
import { scopeI18nKey, type ApiScope } from '@/Pages/Integrations/apiClientsApi';
import {
  DEVELOPER_APP_CATEGORIES,
  type DeveloperAppCategory,
} from '../devportalApi';
import {
  MARKETPLACE_PERMISSIONS,
  MARKETPLACE_ROUTES,
  invalidateMarketplaceInstallation,
  useMarketplaceInstallationsQuery,
  useMarketplaceListingsQuery,
  type ConfigureInstallationBody,
  type ConsentUpgradeBody,
  type InstallMarketplaceAppBody,
  type MarketplaceInstallationWithSecret,
  type MarketplaceListing,
  type UninstallMarketplaceAppBody,
} from '../marketplaceApi';
import { apiMessage, formatDateTime, sameScopeSet, toneForClientStatus, toneForInstallationStatus } from '../lib';

/**
 * Settings › Marketplace — ADR-0027, the installing tenant's side: browsing
 * the platform's catalogue of reviewed third-party apps, and this venue's own
 * installations of them.
 *
 * Two sections, not two URL tabs — both are read off `marketplace.browse` /
 * `.install`, which `tabRoutes.ts`'s `SETTINGS_TABS` entry already gates as a
 * pair, and neither section is deep enough on its own to earn a bookmark
 * `routedTabs.tsx`'s "a tab is a place" argument was built for; the row a
 * person opens here is the listing or the installation, and those already
 * open as dialogs the way every other Settings panel's rows do.
 */
export default function MarketplacePanel() {
  const { canAny } = usePermission();
  const canBrowse = canAny(MARKETPLACE_PERMISSIONS.browse);
  const canSeeInstalled = canAny(MARKETPLACE_PERMISSIONS.install);

  return (
    <div className="space-y-8">
      {canBrowse && <BrowseSection />}
      {canSeeInstalled && <InstalledSection />}
    </div>
  );
}

// -------------------------------------------------------------- browse

function BrowseSection() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canInstall = can(MARKETPLACE_PERMISSIONS.install);

  const [category, setCategory] = useState<DeveloperAppCategory | ''>('');
  const [installing, setInstalling] = useState<MarketplaceListing | null>(null);
  const [issuedSecret, setIssuedSecret] = useState<MarketplaceInstallationWithSecret | null>(null);

  const { data, isFetching, isError, error, refetch } = useMarketplaceListingsQuery(
    category ? { category } : undefined,
  );
  const installedAppIds = useInstalledAppIds();

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t('marketplace.browseTitle')}</h2>
        <p className="text-sm text-ink-muted">{t('marketplace.browseSubtitle')}</p>
      </div>

      <Field label={t('devportal.category')} controlId="marketplace-category">
        <Select
          value={category}
          onChange={(e) => setCategory(e.target.value as DeveloperAppCategory | '')}
          className="w-56"
        >
          <option value="">{t('marketplace.allCategories')}</option>
          {DEVELOPER_APP_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {t(`devportal.categories.${c}`)}
            </option>
          ))}
        </Select>
      </Field>

      <PanelResult
        loading={isFetching}
        error={isError ? apiMessage(error, t('devportal.states.loadFailed')) : null}
        count={data?.length ?? 0}
        onRetry={refetch}
        filtered={Boolean(category)}
        onClearFilters={() => setCategory('')}
        emptyTitle={t('marketplace.emptyTitle')}
        emptyHint={t('marketplace.emptyHint')}
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('marketplace.app')}</TH>
                <TH>{t('devportal.category')}</TH>
                <TH>{t('marketplace.publisher')}</TH>
                <TH>{t('devportal.requestedScopes')}</TH>
                <TH className="text-end">{t('common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {(data ?? []).map((listing) => {
                const alreadyInstalled = installedAppIds.has(listing.developerAppId);
                return (
                  <TR key={listing.id}>
                    <TD className="font-medium">{listing.name}</TD>
                    <TD className="text-sm text-ink-muted">{t(`devportal.categories.${listing.category}`)}</TD>
                    <TD className="text-sm text-ink-muted">{listing.publisherName}</TD>
                    <TD className="text-sm text-ink-muted">{listing.requestedScopes.join(', ')}</TD>
                    <TD className="text-end">
                      {canInstall &&
                        (alreadyInstalled ? (
                          <Badge tone="neutral">{t('marketplace.alreadyInstalled')}</Badge>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => setInstalling(listing)}>
                            {t('marketplace.install')}
                          </Button>
                        ))}
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>

      {installing && (
        <InstallDialog
          listing={installing}
          onClose={() => setInstalling(null)}
          onInstalled={(issued) => {
            setInstalling(null);
            setIssuedSecret(issued);
          }}
        />
      )}

      <SecretRevealDialog
        open={issuedSecret !== null}
        secret={issuedSecret?.apiClient.clientSecret ?? null}
        title={t('marketplace.secretTitle')}
        intro={t('marketplace.secretIntro')}
        onClose={() => setIssuedSecret(null)}
      />
    </section>
  );
}

/** Every developerAppId this tenant already has a live (non-uninstalled) installation of, so Browse can say "Installed" instead of offering a second one the backend would 409 on. */
function useInstalledAppIds(): Set<string> {
  const { can } = usePermission();
  const { data } = useMarketplaceInstallationsQuery(undefined, { skip: !can(MARKETPLACE_PERMISSIONS.install) });
  return new Set((data ?? []).filter((i) => i.status !== 'UNINSTALLED').map((i) => i.developerAppId));
}

function InstallDialog({
  listing,
  onClose,
  onInstalled,
}: {
  listing: MarketplaceListing;
  onClose: () => void;
  onInstalled: (issued: MarketplaceInstallationWithSecret) => void;
}) {
  const { t } = useTranslation();
  const branches = useBranches();
  const [branchId, setBranchId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      // ALWAYS the listing's own requestedScopes, verbatim — never a subset or
      // superset a picker could construct. ADR-0027: installing IS consenting
      // to exactly what the version asks, and the backend refuses anything else.
      const body: InstallMarketplaceAppBody = {
        acceptedScopes: listing.requestedScopes,
        ...(branchId ? { branchId } : {}),
      };
      const { data } = await api.post<MarketplaceInstallationWithSecret>(
        MARKETPLACE_ROUTES.install(listing.developerAppId),
        body,
      );
      toast.success(t('marketplace.installed'));
      onInstalled(data);
    } catch (requestError) {
      setError(apiMessage(requestError, t('devportal.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('marketplace.installTitle', { name: listing.name })}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void submit()}>
            {t('marketplace.confirmInstall')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-muted">{t('marketplace.installExplainer')}</p>
        <div>
          <p className="mb-1.5 text-sm font-medium">{t('marketplace.thisAppWillBeAbleTo')}</p>
          <ul className="space-y-1 rounded-lg border p-2 text-sm">
            {listing.requestedScopes.map((scope) => (
              <li key={scope}>
                <span dir="ltr" className="block font-mono text-xs text-ink-muted">
                  {scope}
                </span>
                <span className="block">
                  {t(`integrations.apiClients.scopeDescription.${scopeI18nKey(scope)}`)}
                </span>
              </li>
            ))}
          </ul>
        </div>
        {branches.length > 0 && (
          <Field
            label={t('integrations.common.branch')}
            hint={t('integrations.apiClients.branchHint')}
          >
            <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">{t('integrations.providers.wholeVenue')}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// -------------------------------------------------------------- installed

function InstalledSection() {
  const { t } = useTranslation();
  const { can } = usePermission();
  // `GET /marketplace/listings` is gated on `marketplace.browse` alone on the
  // backend, a DIFFERENT key from the `marketplace.install` this whole section
  // is already gated on by its caller. A holder of `.install` without
  // `.browse` — unusual, but the two are separate keys precisely so a venue
  // CAN grant them apart — would otherwise fire a query the backend refuses.
  // Skipped rather than firing and eating the 403: `listingByAppId` then stays
  // empty, rows fall back to showing the raw app id, and the re-consent
  // comparison below has nothing to compare against and stays silent. That is
  // a real limitation of holding one key without the other, not a bug — noted
  // in the report rather than routed around, since there is no other public
  // read of "what does this app's current listing request" a browse-less
  // installer is entitled to.
  const canBrowse = can(MARKETPLACE_PERMISSIONS.browse);
  const { data, isFetching, isError, error, refetch } = useMarketplaceInstallationsQuery();
  const { data: listings } = useMarketplaceListingsQuery(undefined, { skip: !canBrowse });
  const [openId, setOpenId] = useState<string | null>(null);

  const listingByAppId = new Map((listings ?? []).map((l) => [l.developerAppId, l]));

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t('marketplace.installedTitle')}</h2>
        <p className="text-sm text-ink-muted">{t('marketplace.installedSubtitle')}</p>
      </div>

      <PanelResult
        loading={isFetching}
        error={isError ? apiMessage(error, t('devportal.states.loadFailed')) : null}
        count={data?.length ?? 0}
        onRetry={refetch}
        emptyTitle={t('marketplace.noInstallationsTitle')}
        emptyHint={t('marketplace.noInstallationsHint')}
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('marketplace.app')}</TH>
                <TH>{t('devportal.status')}</TH>
                <TH>{t('marketplace.credentialHealth')}</TH>
                <TH>{t('marketplace.reconsentNeeded')}</TH>
              </TR>
            </THead>
            <tbody>
              {(data ?? []).map((installation) => {
                const listing = listingByAppId.get(installation.developerAppId);
                const needsConsent =
                  listing != null && !sameScopeSet(installation.grantedScopes, listing.requestedScopes);
                return (
                  <TR
                    key={installation.id}
                    className="cursor-pointer"
                    onClick={() => setOpenId(installation.id)}
                  >
                    <TD className="font-medium">{listing?.name ?? installation.developerAppId}</TD>
                    <TD>
                      <Badge tone={toneForInstallationStatus(installation.status)}>
                        {t(`marketplace.installationStatus.${installation.status}`)}
                      </Badge>
                    </TD>
                    <TD>
                      <Badge tone={toneForClientStatus(installation.apiClient.status)}>
                        {t(`marketplace.clientStatus.${installation.apiClient.status}`)}
                      </Badge>
                    </TD>
                    <TD>
                      {needsConsent && installation.status !== 'UNINSTALLED' && (
                        <Badge tone="warning">{t('marketplace.reconsentBadge')}</Badge>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>

      {openId && (
        <InstallationDetail
          id={openId}
          listing={
            (data ?? []).find((i) => i.id === openId)
              ? listingByAppId.get((data ?? []).find((i) => i.id === openId)!.developerAppId) ?? null
              : null
          }
          onClose={() => setOpenId(null)}
        />
      )}
    </section>
  );
}

function InstallationDetail({
  id,
  listing,
  onClose,
}: {
  id: string;
  listing: MarketplaceListing | null;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { confirm } = useConfirm();
  const { can } = usePermission();
  const branches = useBranches();
  const canConfigure = can(MARKETPLACE_PERMISSIONS.configure);
  const canUninstall = can(MARKETPLACE_PERMISSIONS.uninstall);
  const canConsent = can(MARKETPLACE_PERMISSIONS.consentUpgrade);

  const { data, isFetching, isError, error, refetch } = useMarketplaceInstallationsQuery(undefined, {
    selectFromResult: ({ data: all, ...rest }) => ({
      data: (all ?? []).find((i) => i.id === id) ?? null,
      ...rest,
    }),
  });
  const reload = () => {
    refetch();
    dispatch(invalidateMarketplaceInstallation(id));
  };

  // `INSTALLATION_VIEW`'s `apiClient` selection carries no `branchId` — the
  // backend exposes a WRITER for the branch fence (`PATCH .../:id`,
  // `marketplace.configure`) but no matching reader anywhere on this tenant's
  // side, so the field this form edits cannot be pre-filled with its current
  // value. Left blank rather than guessed; see the report for this gap.
  const [branchId, setBranchId] = useState('');
  const [consenting, setConsenting] = useState(false);

  const configure = async () => {
    try {
      const body: ConfigureInstallationBody = { branchId: branchId || null };
      await api.patch(MARKETPLACE_ROUTES.installation(id), body);
      toast.success(t('devportal.saved'));
      reload();
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('devportal.errors.actionFailed')));
    }
  };

  const uninstall = async () => {
    const ok = await confirm({
      title: t('marketplace.uninstallTitle'),
      body: t('marketplace.uninstallBody'),
      confirmLabel: t('marketplace.uninstall'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const body: UninstallMarketplaceAppBody = {};
      await api.post(MARKETPLACE_ROUTES.uninstall(id), body);
      toast.success(t('marketplace.uninstalled'));
      reload();
      onClose();
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('devportal.errors.actionFailed')));
    }
  };

  const needsConsent =
    listing != null && data != null && !sameScopeSet(data.grantedScopes, listing.requestedScopes);

  return (
    <Dialog
      open
      onClose={onClose}
      title={listing?.name ?? t('marketplace.installationTitle')}
      className="max-w-2xl"
      footer={
        <div className="flex flex-wrap justify-between gap-2">
          {data && data.status !== 'UNINSTALLED' && canUninstall && (
            <Button variant="danger" onClick={() => void uninstall()}>
              {t('marketplace.uninstall')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      {isFetching && !data && <LoadingState />}
      {isError && !data && (
        <ErrorState message={apiMessage(error, t('devportal.states.loadFailed'))} onRetry={reload} />
      )}
      {data && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={toneForInstallationStatus(data.status)}>
              {t(`marketplace.installationStatus.${data.status}`)}
            </Badge>
            <Badge tone={toneForClientStatus(data.apiClient.status)}>
              {t(`marketplace.clientStatus.${data.apiClient.status}`)}
            </Badge>
          </div>

          {/*
              ADR-0027's central proof, made visible: the platform APPROVING a
              new version never touches what this tenant already granted. Only
              this tenant's own explicit re-consent does — the two verbs read
              differently on purpose.
          */}
          {needsConsent && data.status !== 'UNINSTALLED' && (
            <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
              <p className="font-medium">{t('marketplace.reconsentTitle')}</p>
              <p>{t('marketplace.reconsentExplainer')}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium text-ink-muted">{t('marketplace.currentlyGranted')}</p>
                  {data.grantedScopes.length === 0 ? (
                    <p className="text-xs">—</p>
                  ) : (
                    <ul className="space-y-1 text-xs">
                      {data.grantedScopes.map((scope) => (
                        <li key={scope}>
                          {t(`integrations.apiClients.scopeDescription.${scopeI18nKey(scope)}`)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-ink-muted">{t('marketplace.publisherNowRequests')}</p>
                  <ul className="space-y-1 text-xs">
                    {listing!.requestedScopes.map((scope) => (
                      <li key={scope}>
                        {t(`integrations.apiClients.scopeDescription.${scopeI18nKey(scope)}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {canConsent && (
                <Button size="sm" onClick={() => setConsenting(true)}>
                  {t('marketplace.reconsent')}
                </Button>
              )}
            </div>
          )}

          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <div className="sm:col-span-2">
              <dt className="text-ink-muted">{t('marketplace.grantedScopes')}</dt>
              <dd className="space-y-1 pt-1">
                {data.grantedScopes.map((scope) => (
                  <div key={scope} className="flex flex-wrap items-baseline gap-x-2">
                    <Badge tone="neutral">
                      <span dir="ltr">{scope}</span>
                    </Badge>
                    <span className="text-sm">
                      {t(`integrations.apiClients.scopeDescription.${scopeI18nKey(scope)}`)}
                    </span>
                  </div>
                ))}
              </dd>
            </div>
            <div>
              <dt className="text-ink-muted">{t('integrations.apiClients.lastUsed')}</dt>
              <dd>{formatDateTime(data.apiClient.lastUsedAt, i18n.language)}</dd>
            </div>
            <div>
              <dt className="text-ink-muted">{t('devportal.submittedAt')}</dt>
              <dd>{formatDateTime(data.createdAt, i18n.language)}</dd>
            </div>
          </dl>

          {data.status !== 'UNINSTALLED' && canConfigure && (
            <section className="space-y-2 border-t pt-4">
              <h3 className="text-sm font-semibold">{t('devportal.sectionConfigure')}</h3>
              <div className="flex flex-wrap items-end gap-2">
                {branches.length > 0 && (
                  <Field
                    label={t('integrations.common.branch')}
                    hint={t('marketplace.branchFenceUnknownHint')}
                  >
                    <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                      <option value="">{t('integrations.providers.wholeVenue')}</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                <Button size="sm" variant="outline" onClick={() => void configure()}>
                  {t('common.save')}
                </Button>
              </div>
            </section>
          )}
        </div>
      )}

      {data && listing && consenting && (
        <ConsentUpgradeDialog
          installationId={data.id}
          targetScopes={listing.requestedScopes}
          onClose={() => setConsenting(false)}
          onConsented={() => {
            setConsenting(false);
            reload();
          }}
        />
      )}
    </Dialog>
  );
}

function ConsentUpgradeDialog({
  installationId,
  targetScopes,
  onClose,
  onConsented,
}: {
  installationId: string;
  targetScopes: ApiScope[];
  onClose: () => void;
  onConsented: () => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: ConsentUpgradeBody = { acceptedScopes: targetScopes };
      await api.post(MARKETPLACE_ROUTES.consentUpgrade(installationId), body);
      toast.success(t('marketplace.reconsented'));
      onConsented();
    } catch (requestError) {
      setError(apiMessage(requestError, t('devportal.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('marketplace.reconsent')}
      className="max-w-md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void submit()}>
            {t('marketplace.confirmReconsent')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-muted">{t('marketplace.reconsentDialogExplainer')}</p>
        <ul className="space-y-1 rounded-lg border p-2 text-sm">
          {targetScopes.map((scope) => (
            <li key={scope}>
              <span dir="ltr" className="block font-mono text-xs text-ink-muted">
                {scope}
              </span>
              <span className="block">
                {t(`integrations.apiClients.scopeDescription.${scopeI18nKey(scope)}`)}
              </span>
            </li>
          ))}
        </ul>
        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
